import type { SessionEntityType, StudentUserInfo, TeacherUserInfo, UserSession } from "@monsterverse/island-types";

import LocalDevStudentGuestUser from "../api/mocks/getStudentGuestUserSession.json";
import LocalDevStudentUser from "../api/mocks/getStudentUserSession.json";
import LocalDevTeacherUser from "../api/mocks/getTeacherUserSession.json";
import { mocksResponse } from "../utils/env";

// these student ID's are necessary to work with local API
export const mockId = "52338d4b89f0de000000dddd";
export const altMockId = "521fc843178a921652000007";

export class UserManager {
  static async getLocalDevUser(entityType: SessionEntityType | "guest") {
    if (!mocksResponse()) {
      throw new Error("[getLocalDevUser] This is only intended for local dev environment");
    }

    let user;
    if (entityType === "guest") {
      user = LocalDevStudentGuestUser as UserSession;
    } else if (entityType === "teacher") {
      user = LocalDevTeacherUser as UserSession;
    } else {
      user = LocalDevStudentUser as UserSession;
    }

    // Use default mock id in Chrome, otherwise use alt mock id for any other browser
    // Note: Only tested on Chrome/Safari. The reliablity of identifying browser using this technique has not been determined.
    // Note: On Edge, the userAgent string contains for example "Edg/135.0.0.0" but is otherwise identical to Chrome's!
    if (navigator.userAgent.indexOf("Chrome") === -1 || navigator.userAgent.indexOf("Edg") !== -1) {
      if (user.type === "student") {
        user.student._id = altMockId;
      } else {
        user.teacher._id = altMockId;
      }
    }

    return user;
  }

  private static _instance: UserManager = new UserManager();
  static get instance(): UserManager {
    return this._instance;
  }

  private _user: UserSession | undefined;
  get user(): UserSession | undefined {
    return this._user;
  }
  set user(user: UserSession | undefined) {
    this._user = user;
  }

  get userId(): string | undefined {
    if (!this._user) return undefined;
    return this._user.type === "teacher" ? this._user.teacher._id : this._user.student._id;
  }

  reset() {
    // reset any previously set user info
    this._user = undefined;
  }

  async syncUser(dataProvider: () => Promise<UserSession>) {
    this._user = await dataProvider();
    return this._user;
  }

  getEntityId(): string | undefined {
    return this.userId;
  }

  getEntityType() {
    return this._user?.type;
  }

  getEntity(): StudentUserInfo | TeacherUserInfo | undefined {
    return this._user?.type === "teacher" ? this._user.teacher : this._user?.student;
  }

  getInviter() {
    return this.user?.type === "student" ? this.user?.inviter : undefined;
  }

  getSchoolId(): string | undefined {
    if (this._user) {
      switch (this._user.type) {
        case "student":
          return this._user.student?.schoolIds?.[0];
        case "teacher":
          return this._user.teacher?.schoolId;
      }
    }
  }
}
